import { isPlatformBrowser } from "@angular/common";
import { inject, Injectable, PLATFORM_ID, signal } from "@angular/core";
import { disconnect, fallback, http, reconnect } from "@wagmi/core";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
import { AnalyticsService } from './../analytics.service';

import { AppKit, ConnectedWalletInfo, UseAppKitAccountReturn } from "@reown/appkit";
import { AppKitNetwork, arbitrum, arbitrumSepolia, bsc, ChainNamespace, polygon, polygonAmoy } from "@reown/appkit/networks";
import { environment } from "../../../environments/environment";
import { CHAINS } from "../../shared/constants/app-enums";
import { IReownConnectionItems } from "../../shared/interfaces/wallet.interface";
import { SolanaAdapter } from "@reown/appkit-adapter-solana";
import { PixelService } from '../pixel.service';
import { EventService } from '../event.service';


type ReownAppKitModule = typeof import("@reown/appkit");
type SolanaAdapterModule = typeof import("@reown/appkit-adapter-solana");
type WagmiAdapterModule = typeof import("@reown/appkit-adapter-wagmi")

const APP_CONFIG = {
    name: 'Seerdex',
    description: 'Seerdex is the world No.1 site for presale',
    url: environment.domain,
    icons: [environment.domain + '/assets/images/pngs/logo.png'],
}

export const FEATURED_WALLET_IDS = [
    'fe68cea63541aa53ce020de7398968566dfe8f3725663a564cac89490247ed49', // Best wallet
    'a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393', // Phantom
    'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // Meta Mask
    "4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0" // Trust wallet
];
@Injectable({
    providedIn: 'root',
})


export class ReownService {
    reownKit!: ReownAppKitModule;

    private readonly _connectionItems = signal<IReownConnectionItems>({} as IReownConnectionItems);
    readonly connectionItems = this._connectionItems.asReadonly();

    private readonly _walletDisconnected = signal(false);
    readonly walletDisconnected = this._walletDisconnected.asReadonly();

    wagmiAdapter!: WagmiAdapter;
    walletConnector!: AppKit;
    projectId: string = environment.reownProductId;
    connectedWalletName: string = "";
    evmWalletAddress: string = "";
    solWalletAddress: string = "";
    solanaAdapter!: SolanaAdapter;
    sendVwoData = false;
    lastAddress: string = "";
    private modalOpenedFromConnectWallet = false;
    // Used to ignore the transient "disconnected" event Reown emits for the
    // just-activated target namespace before the user actually completes the connection.
    private isModalOpen = false;
    // When MetaMask switches namespace (EVM → Solana), defer the EVM disconnect until
    // the new namespace is confirmed. If the user cancels, MetaMask stays active.
    private pendingEvmDisconnect = false;
    // Set immediately before the deferred wagmi disconnect so the subscribeAccount
    // listener doesn't treat that expected event as a global wallet teardown.
    private suppressNextEvmDisconnect = false;

    private readonly _platformId = inject(PLATFORM_ID);
    private readonly analyticsService = inject(AnalyticsService);
    private readonly pixelService = inject(PixelService);
    private readonly eventService = inject(EventService);

    constructor() {
        if (isPlatformBrowser(this._platformId)) {
            this.projectId = environment.reownProductId;
            import("@reown/appkit").then((reownkit) => {
                this.reownKit = reownkit;
                import("@reown/appkit-adapter-wagmi").then((wagmiAdapter) => {
                    import("@reown/appkit-adapter-solana").then((solAdapter) => {
                        this.initialzeWalletAdapter(solAdapter, wagmiAdapter);
                    })
                })
            })
        }
    }


    async initialzeWalletAdapter(solAdapter: SolanaAdapterModule, wagmiAdapter: WagmiAdapterModule) {
        try {
            this.solanaAdapter = new solAdapter.SolanaAdapter();
            const { mainnet, solana, bsc, sepolia, bscTestnet, solanaDevnet } = await import("@reown/appkit/networks");
            const allNetworksEVM = environment.production ? [mainnet, bsc, arbitrum, polygon] : [sepolia, bscTestnet, arbitrumSepolia, polygonAmoy];
            const SOL = environment.production ? [solana] : [solanaDevnet];

            this.wagmiAdapter = new wagmiAdapter.WagmiAdapter({
                projectId: this.projectId,
                networks: allNetworksEVM,
                transports: {
                    [environment.eth.chainIdInt]: fallback([http(environment.eth.rpcURL), http(environment.eth.backupRPC)]),
                    [environment.bsc.chainIdInt]: fallback([http(environment.bsc.rpcURL), http(environment.bsc.backupRPC)]),
                    [environment.polygon.chainIdInt]: fallback([http(environment.polygon.rpcURL), http(environment.polygon.backupRPC)]),
                    [environment.arbitrum.chainIdInt]: fallback([http(environment.arbitrum.rpcURL), http(environment.arbitrum.backupRPC)]),
                },
            })
            this._connectionItems.set({ wagmiConfig: this.wagmiAdapter.wagmiConfig })
            this.createAppKitInstance([...SOL, ...allNetworksEVM] as AppKitNetwork[]);

        } catch (error) {
        }
    }

    createAppKitInstance(networks: AppKitNetwork[]) {

        this.walletConnector = this.reownKit.createAppKit({
            adapters: [this.solanaAdapter, this.wagmiAdapter],
            networks: [...networks] as [AppKitNetwork, ...AppKitNetwork[]],
            metadata: APP_CONFIG,
            projectId: this.projectId,
            themeMode: 'dark',
            enableNetworkSwitch: true,
            enableWalletConnect: true,
            enableCoinbase: false,
            featuredWalletIds: [...FEATURED_WALLET_IDS.filter(item => !this.getExcludedIds().includes(item))],
            features: {
                email: false,
                socials: false,
                analytics: false,
                history: false,
                allWallets: true,
            },
        });
        this.subscribeEvents();
    }

    subscribeEvents() {

        this.walletConnector.subscribeWalletInfo((item: ConnectedWalletInfo | undefined) => {
            this.connectedWalletName = item?.name as string;
        })

        // On modal close, if a pending namespace switch was never claimed by a successful
        // connect event, clear the flag (with a small delay so subscribeAccount has a
        // chance to claim it first on a successful switch).
        this.walletConnector.subscribeState?.((state: { open: boolean }) => {
            this.isModalOpen = state.open;
            if (!state.open && this.pendingEvmDisconnect) {
                setTimeout(() => {
                    if (this.pendingEvmDisconnect) {
                        this.pendingEvmDisconnect = false;
                    }
                }, 250);
            }
        });


        this.walletConnector.subscribeAccount(async (item: UseAppKitAccountReturn) => {
            // subscribeAccount() (no namespace) returns only the activeChain's account state.
            // Because solanaAdapter is registered first, activeChain defaults to 'solana', so
            // a wallet connecting on a different namespace (e.g. mobile MetaMask via WC lands
            // on eip155) leaves item.isConnected=false even after success. getActiveSessions()
            // inspects each namespace directly and is the real source of truth.
            const _wallets = await this.getActiveSessions();
            // console.log('************', item)
            const solAddress = _wallets['solana'] || '';
            const evmAddress = _wallets['eip155'] || '';
            const effectivelyConnected = !!(evmAddress || solAddress);
            if (!effectivelyConnected && item.status === "disconnected" && !this.modalOpenedFromConnectWallet) {
                this.walletConnector.close();
                this.removeAppKitReferenceFromLocalStorage();
                this.clearIndexedDB();
            }
            if (effectivelyConnected) {
                const activeChain = item?.caipAddress?.includes('solana') || (!evmAddress && solAddress)
                    ? CHAINS.SOL
                    : CHAINS.EVM;
                const activeAddress = activeChain === CHAINS.EVM ? evmAddress : solAddress;
                // Deferred MetaMask disconnect: new namespace is confirmed, release the
                // wagmi/EVM session and suppress the resulting "disconnected" event.
                if (this.pendingEvmDisconnect && activeChain === CHAINS.SOL) {
                    this.pendingEvmDisconnect = false;
                    this.suppressNextEvmDisconnect = true;
                    await disconnect(this.wagmiAdapter.wagmiConfig).catch(() => { });
                }
                if (evmAddress && this.wagmiAdapter?.wagmiConfig?.state?.status !== "connected") {
                    try {
                        await reconnect(this.wagmiAdapter?.wagmiConfig);
                    } catch (err) {
                        if (this.isStaleWcSessionError(err)) {
                            await this.purgeStaleWcState();
                            try {
                                await reconnect(this.wagmiAdapter?.wagmiConfig);
                            } catch { }
                        }
                    }
                }
                this._walletDisconnected.set(false);
                this.solWalletAddress = solAddress;
                this.evmWalletAddress = evmAddress;
                this._connectionItems.set({ wagmiConfig: this.wagmiAdapter?.wagmiConfig, walletAddressEVM: evmAddress, walletAddressSOL: solAddress, appKitInstance: this.walletConnector, activeChain: activeChain })

                if (this.lastAddress !== activeAddress) {
                    this.eventService.broadcastEvent("UPDATE_CONSENT", true);
                    this.lastAddress = activeAddress;
                    this.pixelService.sendPixelEvent('wallet_connect_event', {
                        action: 'success',
                        wallet_address: activeAddress,
                        wallet_type: activeChain,
                        wallet_name: this.connectedWalletName,
                        chain_symbol: activeChain,
                    });
                }
            } else if (item.status === "disconnected") {
                // Skip teardown for the expected EVM disconnect that follows a successful
                // namespace switch from MetaMask → Solana.
                if (this.suppressNextEvmDisconnect) {
                    this.suppressNextEvmDisconnect = false;
                    const refreshed = await this.getActiveSessions();
                    const refreshedSol = refreshed['solana'] || '';
                    const refreshedEvm = refreshed['eip155'] || '';
                    this.evmWalletAddress = refreshedEvm;
                    this.solWalletAddress = refreshedSol;
                    this._connectionItems.set({
                        wagmiConfig: this.wagmiAdapter?.wagmiConfig,
                        walletAddressEVM: refreshedEvm,
                        walletAddressSOL: refreshedSol,
                        appKitInstance: this.walletConnector,
                        activeChain: refreshedSol ? CHAINS.SOL : CHAINS.EVM,
                    });
                    return;
                }
                // Ignore transient "disconnected" events emitted by Reown while the connect
                // modal is open — without this we'd clear address state while wagmi/cookies
                // still hold the prior session.
                if (this.isModalOpen) {
                    return;
                }
                this.evmWalletAddress = "";
                this.solWalletAddress = "";
                this.lastAddress = "";
                this._walletDisconnected.set(true);
                this._connectionItems.set({ wagmiConfig: this.wagmiAdapter?.wagmiConfig, walletAddressEVM: "", walletAddressSOL: "", appKitInstance: this.walletConnector, activeChain: CHAINS.EVM });
            }
        })

    }

    async connectWallet(chain: "SOL" | "ETH" | "" = "") {
        try {
            this.sendVwoData = true;
            this.analyticsService.vwoData('walletTypeSelect', { walletType: chain || "EVM/SOL" });
            const namespace = chain === 'ETH' ? 'eip155' : 'solana'
            // MetaMask isn't registered with Reown on the Solana namespace, so it can't hold
            // EVM and Solana sessions simultaneously. Disconnecting MetaMask up front would
            // strand the user if they then reject the new prompt — defer the EVM disconnect
            // until the new namespace is confirmed (handled in subscribeAccount).
            const isSwitchingFromMetaMask =
                !!chain &&
                this.connectedWalletName?.toLowerCase() === 'metamask wallet' &&
                !!this.evmWalletAddress;
            if (isSwitchingFromMetaMask) {
                this.pendingEvmDisconnect = true;
            }
            this.modalOpenedFromConnectWallet = true;
            if (!chain) this.walletConnector.open({ view: 'Connect' });
            if (chain) this.walletConnector.open({ view: 'Connect', namespace: namespace });
        } catch (e) {
            this.analyticsService.vwoData('walletConnectionFailed', { walletType: chain });
            this.pixelService.sendPixelEvent('wallet_connect_event', {
                action: 'failed',
                wallet_type: chain,
                wallet_name: chain || 'EVM/SOL',
                failure_reason: e instanceof Error ? e.message : 'Wallet connection failed',
            });
        }
    }

    async disconnectWallet(): Promise<void> {
        try {
            const walletType = this.evmWalletAddress ? 'EVM' : 'SOL';
            const logAddress = this.evmWalletAddress || this.solWalletAddress;
            this.analyticsService.vwoData('disconnectWallet', { walletAddress: logAddress });

            await this.walletConnector.disconnect();

            this.removeAppKitReferenceFromLocalStorage();
            this.clearIndexedDB();
            this.deleteCookie('subid');

            if (walletType === 'EVM') {
                await disconnect(this.wagmiAdapter.wagmiConfig).catch(() => { });
            }

            this.pixelService.sendPixelEvent('wallet_connect_event', {
                action: 'disconnected',
                wallet_address: logAddress,
                wallet_type: walletType,
                wallet_name: this.connectedWalletName,
                chain_symbol: walletType,
            });

        } catch (error) {
        }
    }


    removeAppKitReferenceFromLocalStorage() {
        if (!isPlatformBrowser(this._platformId)) return;
        const keysToRemove: string[] = [];

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && (key.startsWith('@appkit') || key.startsWith('wagmi'))) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => {
            localStorage.removeItem(key);
        });
    }

    async getActiveSessions() {
        const activeChainNameSpaces = await this.walletConnector?.chainNamespaces;
        const addresses: { [key: string]: string } = {};
        if (activeChainNameSpaces.length) {
            activeChainNameSpaces.forEach(async (item: ChainNamespace) => {
                this.walletConnector.getAddress()
                const _address = this.walletConnector?.getAddress(item);
                if (_address) addresses[item] = _address
            })
        }
        return addresses;
    }

    async resetWcConnection() {
        this.walletConnector.resetWcConnection();
    }

    private isStaleWcSessionError(err: unknown): boolean {
        const msg = err instanceof Error ? err.message : String(err ?? '');
        return /no matching key/i.test(msg) || /session topic doesn't exist/i.test(msg);
    }

    private async purgeStaleWcState(): Promise<void> {
        try { await this.resetWcConnection(); } catch { }
        this.removeAppKitReferenceFromLocalStorage();
        await this.clearIndexedDB();
    }

    async clearIndexedDB() {
        if (!isPlatformBrowser(this._platformId)) return;
        const databases = await window.indexedDB.databases();
        for (const db of databases) {
            if (db.name) {
                await new Promise<void>((resolve, reject) => {
                    const request = window.indexedDB.deleteDatabase(db.name!);
                    request.onsuccess = () => resolve();
                    request.onerror = () => reject(request.error);
                    // onblocked fires when another tab still holds the DB open; resolve
                    // anyway so the disconnect flow doesn't hang.
                    request.onblocked = () => resolve();
                });
            }
        }
    }

    getExcludedIds(): string[] {
        if (!isPlatformBrowser(this._platformId)) return [];
        const excluded_ids = []
        //@ts-ignore
        const isTrustWallet = window.ethereum?.providers?.find((item: { isTrust: boolean }) => item.isTrust);
        //@ts-ignore
        const isMetamask = window.ethereum?.providers?.find((item: { isMetamask: boolean }) => item.isMetamask);
        //@ts-ignore
        const isPhantom = window.phantom?.solana?.isPhantom || window.phantom?.ethereum?.isPhantom;
        if (isMetamask) {
            excluded_ids.push("c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96")
        }
        if (isTrustWallet) {
            excluded_ids.push("4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0");
        }
        if (isPhantom) {
            excluded_ids.push("a797aa35c0fadbfc1a53e7f675162ed5226968b44a19ee3d24385c64d1d3c393")
        }
        return excluded_ids
    }

    deleteCookie(name: string, path: string = '/', domain?: string): void {
        if (typeof document === 'undefined') return;

        let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0; path=${path}`;
        if (domain) {
            cookieString += `; domain=${domain}`;
        }
        document.cookie = cookieString;
    }
}
